import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, LayoutDashboard, Headphones, ShieldCheck } from "lucide-react";
import { fetchMe, ADMIN_AUTH_EVENT } from "@/lib/admin";

export const ADMIN_LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testId: "admin-menu-dashboard" },
  { to: "/admin/books", label: "Review books", icon: Headphones, testId: "admin-menu-books" },
];

export function useIsAdmin() {
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    const check = () => fetchMe().then(() => setIsAdmin(true)).catch(() => setIsAdmin(false));
    check();
    window.addEventListener(ADMIN_AUTH_EVENT, check);
    return () => window.removeEventListener(ADMIN_AUTH_EVENT, check);
  }, []);
  return isAdmin;
}

export default function AdminMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => !ref.current?.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="nav-admin-menu"
        className={`flex items-center gap-1.5 font-mono-ui text-xs uppercase tracking-[0.18em] transition-colors hover:text-primary-gold ${pathname.startsWith("/admin") ? "text-primary-gold" : "text-soft"}`}
      >
        <ShieldCheck className="w-3.5 h-3.5" /> Admin <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-4 w-56 border border-white/10 bg-[#121212]/95 backdrop-blur-xl py-2 shadow-2xl fade-up" data-testid="admin-menu-dropdown">
          {ADMIN_LINKS.map(({ to, label, icon: Icon, testId }) => (
            <Link key={to} to={to} data-testid={testId} className="flex items-center gap-3 px-5 py-3 font-mono-ui text-xs uppercase tracking-[0.16em] text-soft hover:text-primary-gold hover:bg-white/5 transition-colors">
              <Icon className="w-4 h-4" /> {label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
